import {
  Avatar,
  Box,
  Chip,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect } from 'react';
import { Link, matchPath, useLocation } from 'react-router-dom';
import { handlerActiveItem, handlerDrawerOpen, useGetMenuMaster } from '~/api/menu';
import IconButton from '~/components/@extended/IconButton';
import { LinkTarget, NavActionType, NavItemType } from '~/types/menu';

interface Props {
  item: NavItemType;
  level: number;
  isParents?: boolean;
}

const NavItem = ({ item, level, isParents = false }: Props) => {
  const theme = useTheme();

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const openItem = menuMaster.openedItem;

  const downLG = useMediaQuery(theme.breakpoints.down('lg'));

  let itemTarget: LinkTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  const { pathname } = useLocation();
  const isSelected = !!matchPath({ path: item.url!, end: false }, pathname) || openItem === item.id;

  useEffect(() => {
    if (pathname === item.url) handlerActiveItem(item.id!);
  }, [pathname, item.url, item.id]);

  const textColor = theme.palette.navigation.text;
  const iconSelectedColor = theme.palette.navigation.textSelected;

  const Icon = item.icon!;
  const itemIcon = item.icon ? (
    <Icon
      style={{
        fontSize: drawerOpen ? '1.125rem' : '1.4rem',
        color: isSelected ? iconSelectedColor : textColor,
      }}
    />
  ) : (
    false
  );

  return (
    <Box sx={{ position: 'relative' }}>
      <Tooltip
        title={item.title}
        placement="right"
        arrow
        disableHoverListener={drawerOpen}
        slotProps={{
          popper: { modifiers: [{ name: 'offset', options: { offset: [0, -14] } }] },
        }}
      >
        <ListItemButton
          component={Link}
          data-cy={item.id}
          to={item.url!}
          target={itemTarget}
          {...(itemTarget === '_blank' && { rel: 'noopener noreferrer' })}
          disabled={item.disabled}
          selected={isSelected}
          sx={{
            zIndex: 1201,
            pl: drawerOpen ? `${level * 28}px` : 1.5,
            py: !drawerOpen && level === 1 ? 1.25 : 1,
            ...(drawerOpen && {
              '&:hover': {
                bgcolor: theme.palette.navigation.background,
              },
              '&.Mui-selected': {
                bgcolor: theme.palette.navigation.background,
                borderRight: `2px solid ${theme.palette.navigation.accent}`,
                color: iconSelectedColor,
                '&:hover': {
                  color: iconSelectedColor,
                  bgcolor: theme.palette.navigation.background,
                },
              },
            }),
            ...(!drawerOpen && {
              '&:hover': {
                bgcolor: 'transparent',
              },
              '&.Mui-selected': {
                '&:hover': {
                  bgcolor: 'transparent',
                },
                bgcolor: 'transparent',
              },
            }),
          }}
          {...(downLG && {
            onClick: () => handlerDrawerOpen(false),
          })}
        >
          {itemIcon && (
            <ListItemIcon
              sx={{
                minWidth: 28,
                color: isSelected ? iconSelectedColor : textColor,
                ...(!drawerOpen && {
                  borderRadius: 1.5,
                  width: 36,
                  height: 36,
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    bgcolor: theme.palette.navigation.background,
                  },
                }),
                ...(!drawerOpen &&
                  isSelected && {
                    bgcolor: theme.palette.navigation.background,
                    '&:hover': {
                      bgcolor: theme.palette.navigation.background,
                    },
                  }),
              }}
            >
              {itemIcon}
            </ListItemIcon>
          )}
          {(drawerOpen || (!drawerOpen && level !== 1)) && (
            <ListItemText
              primary={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: isSelected ? iconSelectedColor : textColor,
                      flex: 1,
                      minWidth: 0,
                      fontWeight: 600,
                    }}
                  >
                    {item.title}
                  </Typography>
                  {item.badge && <Box sx={{ flexShrink: 0 }}>{item.badge}</Box>}
                </Box>
              }
            />
          )}
          {(drawerOpen || (!drawerOpen && level !== 1)) && !item.badge && item.chip && (
            <Chip
              color={item.chip.color}
              variant={item.chip.variant}
              size={item.chip.size}
              label={item.chip.label}
              avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
            />
          )}
        </ListItemButton>
      </Tooltip>
      {(drawerOpen || (!drawerOpen && level !== 1)) &&
        item?.actions &&
        item?.actions.map((action, index) => {
          const ActionIcon = action.icon!;
          const callAction = action?.function;
          return (
            <IconButton
              key={index}
              {...(action.type === NavActionType.FUNCTION && {
                onClick: (event) => {
                  event.stopPropagation();
                  callAction();
                },
              })}
              {...(action.type === NavActionType.LINK && {
                component: Link,
                to: action.url,
                target: action.target ? '_blank' : '_self',
                ...(action.target && { rel: 'noopener noreferrer' }),
              })}
              color="secondary"
              variant="outlined"
              sx={{
                position: 'absolute',
                top: 12,
                right: 20,
                zIndex: 1202,
                width: 20,
                height: 20,
                mr: -1,
                ml: 1,
                color: 'secondary.dark',
                borderColor: isSelected ? 'primary.light' : 'secondary.light',
                '&:hover': { borderColor: isSelected ? 'primary.main' : 'secondary.main' },
              }}
            >
              <ActionIcon style={{ fontSize: '0.625rem' }} />
            </IconButton>
          );
        })}
    </Box>
  );
};

export default NavItem;
